import React, { useState } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";

import { FormValidationRules } from "../../utils/types/FormValidationRules";
import { getValidate } from "../../utils/validation/getValidate";
import { FailureSeverity, ValidationFailure } from "../../utils/validation/ValidationResult";
import { DropdownOption } from "../inputs/dropdown/DropdownOption";
import { SearchDropdown } from "../inputs/dropdown/SearchDropdown";
import { Label } from "../inputs/label/Label";
import { Stack } from "../layouts/stack/Stack";
import { FormErrorMessage } from "./FormErrorMessage";
import { FormFailureMessages } from "./FormFailureMessages";
import { getFormLabelText } from "./getFormLabelText";
import { useFormWatch } from "./useFormWatch";

export interface FormDropdownProps {
    label: string;
    name: string;
    options: DropdownOption[];
    control: Control;
    errors: FieldErrors;
    watch?: FieldErrors;
    rules?: FormDropdownRules;
    placeholder?: string;
    disabled?: boolean;
    dependsOn?: string[];
    disableSort?: boolean;
    disableClear?: boolean;
    onChange?(value: string): void;
    testId?: string;
}

export const FormDropdown: React.FC<FormDropdownProps> = ({
    label,
    name,
    options,
    control,
    errors,
    rules,
    placeholder,
    disabled,
    dependsOn,
    disableSort,
    onChange,
    testId,
    disableClear
}: FormDropdownProps) => {
    const [warnings, setWarnings] = useState<ValidationFailure[]>([]);

    const clearWarnings = () => {
        setWarnings([]);
    };

    const handleOnChange = () => {
        if (onChange) {
            onChange("");
            clearWarnings();
        }
    };

    if (dependsOn) {
        useFormWatch({
            name,
            control,
            dependsOn,
            onClear: handleOnChange
        });
    }

    const render = ({ value, onChange: onFormChange }: { value: string; onChange(value: string): void }) => {
        const handleChange = (option?: DropdownOption) => {
            clearWarnings();
            onFormChange(option?.key ?? "");

            if (onChange) {
                onChange(option?.key ?? "");
            }
        };

        return (
            <Stack>
                <Label disabled={disabled}>{getFormLabelText(label, !!rules?.required)}</Label>
                <SearchDropdown
                    name={name}
                    errors={errors}
                    placeholder={placeholder}
                    testId={testId || name}
                    options={options}
                    selectedKey={value}
                    onChange={handleChange}
                    disabled={disabled}
                    disableSort={disableSort}
                    disableClear={disableClear}
                />
            </Stack>
        );
    };

    return (
        <Stack>
            <Controller
                name={name}
                control={control}
                rules={{
                    ...rules,
                    validate: getValidate<string, FormDropdownRules>(
                        rules,
                        control.formStateRef.current.submitCount,
                        setWarnings
                    )
                }}
                render={render}
            />
            <FormErrorMessage name={name} errors={errors} testId={`${name}-error`} />
            <FormFailureMessages failures={warnings} severity={FailureSeverity.Warning} testId={`${name}-warning`} />
        </Stack>
    );
};

export type FormDropdownRules = FormValidationRules<string>;
